/**
 * Distance to a 2D polyline, precomputed on a grid.
 *
 * The cut has to ask "how far is this voxel from the drawn line?" once per
 * voxel — over a million times. Walking every segment for every voxel is a
 * second of work. Because the cut is a prismatic sweep, the answer depends
 * only on the voxel's position in the plane perpendicular to the view, so the
 * distance is computed once on a 2D grid and then read back with bilinear
 * interpolation. That turns a million-by-segments problem into a million
 * lookups.
 */
export class PolylineDistance2D {
  private readonly values: Float32Array;
  private readonly cols: number;
  private readonly rows: number;

  constructor(
    points: ReadonlyArray<readonly [number, number]>,
    private readonly uMin: number,
    private readonly vMin: number,
    uMax: number,
    vMax: number,
    private readonly cell: number,
  ) {
    this.cols = Math.max(2, Math.ceil((uMax - uMin) / cell) + 1);
    this.rows = Math.max(2, Math.ceil((vMax - vMin) / cell) + 1);
    this.values = new Float32Array(this.cols * this.rows);

    for (let row = 0; row < this.rows; row++) {
      const v = vMin + row * cell;
      for (let col = 0; col < this.cols; col++) {
        const u = uMin + col * cell;
        this.values[row * this.cols + col] = distanceToPolyline(points, u, v);
      }
    }
  }

  /** Bilinearly interpolated distance at (u, v). */
  sample(u: number, v: number): number {
    const fu = (u - this.uMin) / this.cell;
    const fv = (v - this.vMin) / this.cell;
    if (fu < 0 || fv < 0 || fu > this.cols - 1 || fv > this.rows - 1) {
      // Outside the sampled window the line is far away by construction.
      return Number.POSITIVE_INFINITY;
    }
    const c0 = Math.floor(fu);
    const r0 = Math.floor(fv);
    const c1 = Math.min(c0 + 1, this.cols - 1);
    const r1 = Math.min(r0 + 1, this.rows - 1);
    const tu = fu - c0;
    const tv = fv - r0;

    const a = this.values[r0 * this.cols + c0] as number;
    const b = this.values[r0 * this.cols + c1] as number;
    const c = this.values[r1 * this.cols + c0] as number;
    const d = this.values[r1 * this.cols + c1] as number;
    const top = a + (b - a) * tu;
    const bottom = c + (d - c) * tu;
    return top + (bottom - top) * tv;
  }
}

export function distanceToPolyline(
  points: ReadonlyArray<readonly [number, number]>,
  u: number,
  v: number,
): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i] as readonly [number, number];
    const b = points[i + 1] as readonly [number, number];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lengthSquared = dx * dx + dy * dy;
    let t = 0;
    if (lengthSquared > 1e-12) {
      t = ((u - a[0]) * dx + (v - a[1]) * dy) / lengthSquared;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
    }
    const px = a[0] + dx * t - u;
    const py = a[1] + dy * t - v;
    const d = Math.sqrt(px * px + py * py);
    if (d < best) best = d;
  }
  return best;
}

/**
 * Extend the first and last segments far past the drawn line, so the sweep
 * always leaves the block on both sides. This is what turns a scribble into a
 * clean break rather than a partial slot.
 */
export function extendPolyline(
  points: ReadonlyArray<readonly [number, number]>,
  distance: number,
): Array<[number, number]> {
  if (points.length < 2) return points.map((p) => [p[0], p[1]] as [number, number]);
  const out: Array<[number, number]> = points.map((p) => [p[0], p[1]]);

  const first = out[0] as [number, number];
  const second = out[1] as [number, number];
  const startDir = normalise(first[0] - second[0], first[1] - second[1]);
  out.unshift([first[0] + startDir[0] * distance, first[1] + startDir[1] * distance]);

  const last = out[out.length - 1] as [number, number];
  const penultimate = out[out.length - 2] as [number, number];
  const endDir = normalise(last[0] - penultimate[0], last[1] - penultimate[1]);
  out.push([last[0] + endDir[0] * distance, last[1] + endDir[1] * distance]);

  return out;
}

function normalise(x: number, y: number): [number, number] {
  const length = Math.hypot(x, y);
  if (length < 1e-9) return [1, 0];
  return [x / length, y / length];
}
